import { useState } from "react";
import { CalendarCheck } from "lucide-react";
import { createCardFromSchedule } from "../../services/scheduleCard";
import type { ScheduleCardStatus } from "../../types/scheduleCard";
import { useAuth } from "../../hooks/auth";
import { useToast } from "../../hooks/toast";
import api from "../../services/api";
import refreshToken from "../../utils/refreshToken";
import { userCanCreateKanbanCard } from "../../utils/permissions";
import { formatScheduleCardStatus } from "../../utils/scheduleSearchDisplay";

type BadgeProps = {
  status: ScheduleCardStatus;
};

export function VisitCardCreatedBadge({ status }: BadgeProps) {
  if (!status.has_card) return null;

  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200">
      <CalendarCheck className="h-3.5 w-3.5" aria-hidden />
      {formatScheduleCardStatus(status)}
    </span>
  );
}

type ButtonProps = {
  scheduleId: number;
  canCreateCard: boolean;
  onCreated: (status: ScheduleCardStatus) => void;
  className?: string;
};

export function VisitCreateCardButton({
  scheduleId,
  canCreateCard,
  onCreated,
  className,
}: ButtonProps) {
  const { user, access_token, dateToExpires } = useAuth();
  const { addToast } = useToast();
  const [isCreating, setIsCreating] = useState(false);

  if (!userCanCreateKanbanCard(user) || !canCreateCard) return null;

  async function handleClick() {
    setIsCreating(true);
    try {
      const token = await refreshToken(dateToExpires, access_token);
      api.defaults.headers.authorization = `Bearer ${token || access_token}`;
      const result = await createCardFromSchedule(api, scheduleId);

      if (result.ok) {
        const nextStatus = {
          has_card: true,
          card_id: result.card.id ?? null,
          can_create_card: false,
        };
        onCreated(nextStatus);
        addToast({
          type: "success",
          title: result.message,
          description: "Card criado no Kanban, Synkronos e BPM.",
        });
        return;
      }

      if (result.reason === "already_has_card") {
        onCreated({
          has_card: true,
          card_id: result.cardId,
          can_create_card: false,
        });
      }

      addToast({
        type: result.reason === "already_has_card" ? "info" : "error",
        title: result.message,
      });
    } finally {
      setIsCreating(false);
    }
  }

  return (
    <button
      type="button"
      className={
        className ??
        "btn-create rounded-lg border border-[#E2E8F0] bg-white px-3 py-2 text-sm font-semibold text-[#2D3748] hover:bg-[#F7FAFC] disabled:opacity-50"
      }
      disabled={isCreating}
      onClick={handleClick}
    >
      {isCreating ? "Criando..." : "Criar card"}
    </button>
  );
}
